/**
 * Session Store (Zustand)
 * Manages session metadata, permissions, tracks list
 */

import { create } from "zustand";
import { Track, DraftTrack } from "@jamroom/shared";

export interface SessionStore {
  // Session metadata
  sessionId: string | null;
  title: string;
  createdAt: string | null;
  permissions: {
    canView: boolean;
    canRecord: boolean;
    isOwner: boolean;
  };
  usersOnline: number;
  ownerToken: string | null;
  participantToken: string | null;
  participantName: string;
  isPrivate: boolean;
  shareCode: string | null;
  expiresAt: string | null;
  serverTimeOffsetMs: number;
  sessionExpired: boolean;
  wsConnected: boolean;

  // Tracks
  tracks: Track[];
  drafts: DraftTrack[];
  loading: boolean;
  error: string | null;

  // Actions
  setSession: (sessionId: string, title: string, options?: {
    isPrivate?: boolean;
    createdAt?: string | null;
    expiresAt?: string | null;
  }) => void;
  setPermissions: (perms: SessionStore["permissions"]) => void;
  setUsersOnline: (count: number) => void;
  setWsConnected: (connected: boolean) => void;
  addTrack: (track: Track) => void;
  removeTrack: (trackId: string) => void;
  addDraft: (draft: DraftTrack) => void;
  removeDraft: (localId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTitle: (title: string) => void;
  setOwnerToken: (token: string | null) => void;
  setParticipant: (participant: { token: string | null; name: string }) => void;
  setShareCode: (shareCode: string | null) => void;
  setExpiresAt: (expiresAt: string | null) => void;
  setServerTimeOffsetMs: (offsetMs: number) => void;
  setSessionExpired: (expired: boolean) => void;
  resetSessionState: () => void;
}

export const useSessionStore = create<SessionStore>((set) => ({
  sessionId: null,
  title: "",
  createdAt: null,
  permissions: {
    canView: true,
    canRecord: false,
    isOwner: false,
  },
  usersOnline: 0,
  ownerToken: localStorage.getItem("jamroom_owner_token"),
  participantToken: null,
  participantName: localStorage.getItem("jamroom_display_name") || "",
  isPrivate: false,
  shareCode: null,
  expiresAt: null,
  serverTimeOffsetMs: 0,
  sessionExpired: false,
  wsConnected: false,
  tracks: [],
  drafts: [],
  loading: false,
  error: null,

  setSession: (sessionId, title, options) =>
    set({
      sessionId,
      title,
      isPrivate: options?.isPrivate ?? false,
      createdAt: options?.createdAt ?? null,
      expiresAt: options?.expiresAt ?? null,
      sessionExpired: false,
      tracks: [],
      drafts: [],
    }),

  setPermissions: (perms) =>
    set({ permissions: perms }),

  setUsersOnline: (count) =>
    set({ usersOnline: count }),

  setWsConnected: (connected) =>
    set({ wsConnected: connected }),

  addTrack: (track) =>
    set((state) => ({
      tracks: [
        ...state.tracks.filter((t) => t.id !== track.id),
        track,
      ],
    })),

  removeTrack: (trackId) =>
    set((state) => ({
      tracks: state.tracks.filter((t) => t.id !== trackId),
    })),

  addDraft: (draft) =>
    set((state) => ({
      drafts: [...state.drafts, draft],
    })),

  removeDraft: (localId) =>
    set((state) => ({
      drafts: state.drafts.filter((d) => d.localId !== localId),
    })),

  setLoading: (loading) =>
    set({ loading }),

  setError: (error) =>
    set({ error }),

  setTitle: (title) =>
    set({ title }),

  setOwnerToken: (token) => {
    if (token) {
      localStorage.setItem("jamroom_owner_token", token);
    } else {
      localStorage.removeItem("jamroom_owner_token");
    }
    set({ ownerToken: token });
  },

  setParticipant: ({ token, name }) => {
    const normalizedName = name.trim().slice(0, 40);
    if (normalizedName) {
      localStorage.setItem("jamroom_display_name", normalizedName);
    }
    set({
      participantToken: token,
      participantName: normalizedName,
    });
  },

  setShareCode: (shareCode) =>
    set({ shareCode }),

  setExpiresAt: (expiresAt) =>
    set({ expiresAt }),

  setServerTimeOffsetMs: (offsetMs) =>
    set({ serverTimeOffsetMs: Number.isFinite(offsetMs) ? Math.round(offsetMs) : 0 }),

  setSessionExpired: (expired) =>
    set({ sessionExpired: Boolean(expired) }),

  resetSessionState: () =>
    set({
      sessionId: null,
      title: "",
      createdAt: null,
      usersOnline: 0,
      participantToken: null,
      shareCode: null,
      expiresAt: null,
      serverTimeOffsetMs: 0,
      sessionExpired: false,
      tracks: [],
      drafts: [],
      loading: false,
      error: null,
      wsConnected: false,
      isPrivate: false,
    }),
}));
